import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { getQualifierVariant, type QualifierVariant } from './getQualifierVariant';

type QualifierGate = {
  qualifierVariant: QualifierVariant | null;
  showQualifier: boolean;
  acknowledge: () => void;
};

// Shared between ContactFormSpoke (popup) and InlineContactModule (desktop inline).
// Both surfaces need the same gate with the same suppression rules, so keep
// the selector + acknowledgement state in one place.
//
// Reset per mount — if a component using this hook unmounts and remounts, the
// user sees the qualifier again. Matches legacy ContactOverlay behavior.
export function useQualifierGate(listing: ListingDetails): QualifierGate {
  const aliasEncoded = listing.aliasEncoded;
  const conversation = useSelector((state: RootReduxState) =>
    aliasEncoded ? state.user?.conversations?.[aliasEncoded] : undefined,
  ) as { satelliteEnabled?: boolean } | undefined;

  const qualifierVariant = getQualifierVariant({
    listing,
    hasConversation: Boolean(conversation),
    satelliteEnabled: Boolean(conversation?.satelliteEnabled),
  });

  const [acknowledged, setAcknowledged] = useState(false);
  const acknowledge = useCallback(() => setAcknowledged(true), []);

  return {
    qualifierVariant,
    showQualifier: qualifierVariant !== null && !acknowledged,
    acknowledge,
  };
}
